import { useQuery } from '@tanstack/react-query'
import { Stack, Text } from '@chakra-ui/react'
import { AuthService } from '@/shared/api/rest/services/auth.service'
import { BranchesService } from '@/shared/api/rest/services/branches.service'
import { CoParentsService } from '@/shared/api/rest/services/co-parents.service'
import ProfileHeader from './components/ProfileHeader'
import ProfileForm from './components/ProfileForm'
import PageContainer from '@/components/PageContainer'
import { PROTECTED_ROUTES } from '@/shared/config/routes'
import RoleRequestSection from './components/RoleRequestSection'
import DataTabs, { TabItem } from './components/DataTabs'
import type { ParentProfile } from './type'

/**
 * Fetch the signed-in profile from /auth/me first, matching the mobile app.
 * Parent-domain fields still come from /parents/me when the user has a parent
 * record, but a missing/stale parent row should not erase the central name.
 */
export function useParentProfileQuery() {
    const { data, isLoading, error } = useQuery({
        queryKey: ['parent-profile'],
        retry: false,
        queryFn: async () => {
            const me = await AuthService.getMe()
            const [parent, coParent, branches] = await Promise.all([
                me.parentId ? AuthService.getParentMe() : Promise.resolve(null),
                CoParentsService.me().catch(() => null),
                me.parentId ? BranchesService.list().catch(() => []) : Promise.resolve([])
            ])
            const branchId = parent?.branchId ?? ''
            const branch = branchId
                ? branches.find((b) => b._id === branchId) ?? {
                      _id: branchId,
                      name: ''
                  }
                : { _id: '', name: '' }

            return {
                ...(parent ?? {}),
                _id: parent?._id ?? me.parentId ?? me.id ?? '',
                firstName: me.firstName ?? parent?.firstName ?? '',
                lastName: me.lastName ?? parent?.lastName ?? '',
                email: me.email ?? '',
                picture: me.picture ?? me.imageUrl ?? '',
                phoneNumber: me.phoneNumber ?? parent?.phoneNumber ?? '',
                branch,
                coParent
            } as ParentProfile & {
                coParent: typeof coParent
            }
        }
    })

    return {
        parentProfile: data,
        loading: isLoading,
        error: error ?? undefined
    }
}

/** Function to Generate Tab Data */
function getTabData({
    parentProfile,
    error
}: {
    parentProfile?: ParentProfile
    error?: boolean
}): TabItem[] {
    const tabs: TabItem[] = []

    // the <ProfileForm /> only works with parent information
    // and not teachers. A teacher only user will get error when
    // findMyProfileAsParent query is called.
    // Hence the hiding section for teachers
    if (!error) {
        tabs.push({
            label: 'Parent',
            content: <Stack maxW="lg">{parentProfile && <ProfileForm />}</Stack>
        })
    }

    // Teacher tab (placeholder)
    tabs.push({
        label: 'Teacher',
        content: <Text mt="1em">Editing profile is coming soon...</Text>
    })

    // Roles tab (always present)
    tabs.push({
        label: 'Roles',
        content: <RoleRequestSection />
    })

    return tabs
}

/** Main Component */
export default function UpdateMyProfile() {
    const { parentProfile, loading, error } = useParentProfileQuery()

    return (
        <PageContainer
            routeConfig={PROTECTED_ROUTES['/user/settings']}
            headerText="Account Details"
            subHeaderText="Update your profile details and request for more roles if needed"
        >
            <Stack maxW="lg">
                <ProfileHeader profile={parentProfile} />
            </Stack>
            {loading ? (
                <Text>Loading profile...</Text>
            ) : (
                <DataTabs data={getTabData({ parentProfile, error: !!error })} />
            )}
        </PageContainer>
    )
}
